import { Component, computed, effect, inject, input, InputSignal, output, untracked, viewChild } from '@angular/core';
import { FieldTree, FormField, FormRoot } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatError } from '@angular/material/form-field';
import { ManualHoursDurationFieldsComponent } from '@app/components/time-reports/time-report-dialog/manual-hours-duration-fields/manual-hours-duration-fields.component';
import { TimeReportFileAttachmentsComponent } from '@app/components/time-reports/time-report-dialog/time-report-form/time-report-file-attachments/time-report-file-attachments.component';
import { ReportSeriesForm } from '@app/shared/components/report-entry/report-series/report-series-form';
import { ReportSummaryComponent } from '@app/shared/components/report-entry/report-summary/report-summary.component';
import {
  formatClock,
  rangeDurationMinutes,
  timeValueToMinutes
} from '@app/shared/components/report-entry/report-time-entry.calc';
import { AuthService } from '@app/shared/services/auth.service';
import { HoursTypeDictionaryService } from '@app/shared/services/hours-type-dictionary-service';
import { ProjectsDataService } from '@app/shared/services/projects-data.service';
import { UsersDataService } from '@app/shared/services/users-data.service';
import { TimeReport } from '@app/shared/types/models/reports/time-report';
import { DropdownItem } from '@app/shared/types/models/shared/dropdown-item';
import { TimePickerValue } from '@app/shared/types/models/shared/time-picker-value';
import { TimeReportingType } from '@app/shared/types/models/shared/time-reporting-type';
import { Task } from '@app/shared/types/models/task/task.model';
import { ReportUserSelector } from '@app/shared/types/report-user-selector';
import { UiDatePickerComponent } from '@app/shared/ui/date-picker/ui-date-picker.component';
import { UiInputComponent } from '@app/shared/ui/input/ui-input.component';
import { UiSelectComponent } from '@app/shared/ui/select/ui-select.component';
import { UiTextareaComponent } from '@app/shared/ui/textarea/ui-textarea.component';
import { TranslatePipe } from '@ngx-translate/core';
import { TimeReportFormModel } from '../time-report-form.types';
import {
  buildProjectTaskPreselection,
  ProjectTaskPreselection,
  ProjectTaskSelectorComponent
} from './project-task-selector/project-task-selector.component';
import { TimeInputSectionComponent } from './time-input-section/time-input-section.component';

@Component({
  selector: 'proffeo-time-report-form',
  templateUrl: './time-report-form.component.html',
  imports: [
    FormField,
    FormRoot,
    MatError,
    MatButtonModule,
    TranslatePipe,
    UiDatePickerComponent,
    UiInputComponent,
    UiSelectComponent,
    UiTextareaComponent,
    ProjectTaskSelectorComponent,
    TimeInputSectionComponent,
    ReportSummaryComponent,
    TimeReportFileAttachmentsComponent,
    ManualHoursDurationFieldsComponent
  ]
})
export class TimeReportFormComponent {
  private readonly hoursTypeDictionary = inject(HoursTypeDictionaryService);
  private readonly projectsService = inject(ProjectsDataService);
  private readonly usersDataService = inject(UsersDataService);
  private readonly authService = inject(AuthService);
  private readonly projectTaskSelector = viewChild(ProjectTaskSelectorComponent);

  protected readonly hourTypes = computed((): DropdownItem<string>[] =>
    this.hoursTypeDictionary.getHoursTypeDictionary(this.includeManualHoursType())
  );
  protected readonly isManualHoursType = computed(() => this.form().hourType().value() === 'Manual');
  protected readonly showManualHoursBreakdown = computed(
    () => this.includeManualHoursType() && this.isManualHoursType()
  );

  /** Company / Project / Task come from the Task context or the edited report and stay read-only. */
  protected readonly isReadonlyMode = computed(() => !!this.task() || !!this.timeReport());
  /** Labels (and ids) of the read-only context, resolved once per Task / report. */
  protected readonly projectTaskPreselection = computed((): ProjectTaskPreselection => {
    const task = this.task();
    const report = this.timeReport();
    return untracked(() => buildProjectTaskPreselection(task, report, this.projectsService));
  });

  protected readonly manualDurationsShowError = computed(
    (): boolean =>
      this.form()().touched() &&
      this.form()
        .manualDurations()
        .errors()
        .some(error => error.kind === 'invalidManualDurations')
  );

  protected readonly selectedUserDisplayName = computed((): string => this.form().userName().value());

  protected readonly summaryDate = computed((): Date | null => this.form().date().value());

  protected readonly summaryCreatedByName = computed(
    (): string =>
      this.timeReport()?.createdBy?.()?.fullName ||
      this.form().userName().value() ||
      this.authService.currentUser()?.fullName ||
      ''
  );

  protected readonly summaryCreatedAt = computed(
    (): Date | null => this.timeReport()?.createdOn ?? this.draftStartedAt()
  );

  protected readonly summaryModeLabelKey = computed((): string => {
    switch (this.selectedValue()) {
      case TimeReportingType.MANUAL:
        return 'time-reports.report-entry.mode-manual';
      case TimeReportingType.RANGE:
        return 'time-reports.report-entry.mode-range';
      default:
        return '';
    }
  });

  protected readonly summaryTimeLabel = computed((): string => {
    if (this.selectedValue() !== TimeReportingType.RANGE) {
      return '';
    }

    const { from, to } = this.form().timeRange().value();
    return `${formatClock(from)} – ${formatClock(to)}`;
  });

  protected readonly summaryDurationMinutes = computed((): number => {
    if (this.selectedValue() === TimeReportingType.MANUAL) {
      return timeValueToMinutes(this.form().manualTime().value());
    }

    if (this.selectedValue() === TimeReportingType.RANGE) {
      const { from, to } = this.form().timeRange().value();
      return Math.max(0, rangeDurationMinutes(from, to, false, 0));
    }

    return 0;
  });

  protected readonly seriesActive = computed((): boolean => !!this.series()?.active());

  /** Number of days the summary covers: the selected series days, or the single report day. */
  protected readonly summaryDays = computed((): number =>
    this.seriesActive() ? (this.series()?.occurrenceDates().length ?? 0) : 1
  );

  /** Per-day minutes; a series break in Range mode is deducted for the preview only. */
  protected readonly summaryPerDayMinutes = computed((): number =>
    Math.max(
      0,
      this.summaryDurationMinutes() - (this.seriesActive() ? (this.series()?.effectiveBreakMinutes() ?? 0) : 0)
    )
  );

  protected readonly summaryProjectLabel = computed(
    (): string => this.projectTaskSelector()?.projectLabel() ?? this.projectTaskPreselection().projectName ?? ''
  );

  protected readonly summaryTaskLabel = computed(
    (): string => this.projectTaskSelector()?.taskLabel() ?? this.projectTaskPreselection().taskName ?? ''
  );

  protected readonly summaryLocationLabel = computed((): string => this.projectTaskSelector()?.locationLabel() ?? '');

  protected readonly selectedCompanyId = computed(() => this.form().projectTask.company().value());
  protected readonly hasSelectedCompany = computed(() => !!this.selectedCompanyId());
  protected readonly companyUserOptions = computed((): DropdownItem<string>[] => {
    if (!this.allowSelectUser()) {
      return [];
    }
    const companyId = this.selectedCompanyId();
    if (!companyId) {
      return [];
    }
    return (this.usersDataService.getCompanyUsers(companyId)() ?? []).map(user => ({
      value: user.id,
      label: user.fullName
    }));
  });

  /** Work-time form of the hosting dialog (model + rules: `timeReportFormSchema`). */
  public readonly form: InputSignal<FieldTree<TimeReportFormModel>> = input.required<FieldTree<TimeReportFormModel>>();
  public readonly selectedValue: InputSignal<TimeReportingType> = input<TimeReportingType>();
  public readonly timeReport: InputSignal<TimeReport> = input<TimeReport>();
  public readonly task: InputSignal<Task> = input<Task>();
  public readonly currentMinuteStep: InputSignal<number> = input<number>(1);
  public readonly includeManualHoursType: InputSignal<boolean> = input(false);
  public readonly allowSelectUser: InputSignal<boolean> = input(false);
  public readonly selectUser: InputSignal<ReportUserSelector | undefined> = input();
  /** Shared series state of the hosting dialog; while active it replaces the single date field. */
  public readonly series: InputSignal<ReportSeriesForm | undefined> = input<ReportSeriesForm | undefined>(undefined);
  /** When the current (new) report draft was started; shown as the summary's creation time. */
  public readonly draftStartedAt: InputSignal<Date> = input<Date>(new Date());
  /** The user switched the hours type away from `Manual` (manual-hours dialog only). */
  public readonly manualHoursTypeLeft = output<void>();

  public constructor() {
    effect(() => {
      if (!this.allowSelectUser()) {
        return;
      }

      const companyId = this.selectedCompanyId();
      if (companyId) {
        untracked(() => this.usersDataService.loadCompanyUsers(companyId));
      }
    });
  }

  /** The selector already reset Project / Task / Location; a user of the previous Company is dropped too. */
  protected onCompanyChange(): void {
    if (this.allowSelectUser()) {
      this.clearSelectedUserIfNotCurrent();
    }
  }

  protected onUserIdSelected(userId: unknown): void {
    if (typeof userId !== 'string') {
      return;
    }
    const selectedUser = (this.usersDataService.getCompanyUsers(this.selectedCompanyId() ?? '')() ?? []).find(
      user => user.id === userId
    );
    if (selectedUser) {
      this.form().userName().value.set(selectedUser.fullName);
    }
  }

  /** Only the breakdown's select can leave `Manual` (the other one is rendered for the remaining types). */
  protected onBreakdownHourTypeChange(hoursType: unknown): void {
    if (hoursType !== 'Manual') {
      this.manualHoursTypeLeft.emit();
    }
  }

  protected onQuickTimeSelected(value: TimePickerValue): void {
    this.form()
      .manualTime()
      .value.set({ ...value });
  }

  private clearSelectedUserIfNotCurrent(): void {
    const selectedUserId = this.form().userId().value();
    if (!selectedUserId || selectedUserId === this.authService.currentUser()?.id) {
      return;
    }

    this.form().userId().value.set(null);
    this.form().userName().value.set('');
  }
}
